import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import SupportFormModal from "@/components/SupportFormModal";

export default function LandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const goApp = () => nav(user && typeof user === "object" ? "/app" : "/login");
  const [showSupport, setShowSupport] = useState(false);
  return (
    <div className="min-h-screen bg-[#0b0e14] text-slate-200 font-mono relative overflow-hidden" data-testid="landing-page">
      <div className="absolute inset-0 trading-grid opacity-40 pointer-events-none"/>
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-blue-600/10 blur-3xl"/>
      <div className="absolute -bottom-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-purple-600/10 blur-3xl"/>

      <header className="relative z-10 px-8 py-4 flex justify-between items-center border-b border-slate-800/60 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-600/20 text-blue-400 rounded-lg border border-blue-500/20"><i className="fa-solid fa-terminal"/></div>
          <span className="font-bold text-white">Institutional Sandbox</span>
        </div>
        <div className="flex gap-3 text-sm items-center">
          <button data-testid="landing-support-btn" onClick={() => setShowSupport(true)}
            className="text-slate-400 hover:text-blue-300 text-sm">
            <i className="fa-solid fa-life-ring mr-1"/> Support
          </button>
          <Link to="/login" data-testid="nav-login" className="text-slate-400 hover:text-white">Sign in</Link>
          <Link to="/register" data-testid="nav-register" className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-md font-bold text-white">Get Started</Link>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-8 pt-20 pb-12 text-center">
        <div className="inline-block px-3 py-1 mb-5 border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs rounded-full">
          <i className="fa-solid fa-bolt mr-1.5"/> Real futures data · Claude Sonnet 4.5 coaching · Institutional ICT framework
        </div>
        <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight leading-tight">
          Practice <span className="text-blue-400">ICT</span> like an<br/>
          <span className="bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">institutional desk</span>
        </h1>
        <p className="mt-5 text-slate-400 text-base max-w-2xl mx-auto leading-relaxed">
          Replay 60 days of real MNQ / MES / NQ / ES / BTC futures bar-by-bar with
          fractal multi-timeframe overlays — FVGs, liquidity sweeps, SMT divergence,
          MSS, premium/discount — and get an AI trade review on every fill.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <button data-testid="hero-cta" onClick={goApp}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-xl font-bold text-white shadow-lg shadow-blue-900/30">
            <i className="fa-solid fa-play mr-1.5"/> Launch the Sandbox
          </button>
          <Link to="/register" className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl font-bold text-slate-200">
            Create free account
          </Link>
        </div>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-8 pb-20 grid md:grid-cols-3 gap-4">
        {[
          { icon: "fa-chart-candlestick", color: "text-amber-400", title: "Real Yahoo data", text: "1m + 5m base bars aggregated client-side. A 5m candle is exactly 5×1m candles." },
          { icon: "fa-brain", color: "text-purple-400", title: "Claude post-trade coach", text: "Every fill returns a structured ICT review with grade A–F and key concept used." },
          { icon: "fa-graduation-cap", color: "text-pink-400", title: "Practice Mode", text: "Random chart snapshots become a 5-question ICT quiz on bias, FVG, SMT, premium/discount." },
          { icon: "fa-trophy", color: "text-emerald-400", title: "Leaderboard", text: "Compete on PnL, win-rate and A-grade trades against the rest of the community." },
          { icon: "fa-bell", color: "text-blue-400", title: "Sound alerts", text: "13 distinct tones for FVG, IFVG, MSS, BOS, BSL/SSL purges, SMT and session opens." },
          { icon: "fa-ruler-vertical", color: "text-rose-400", title: "Long/Short planner", text: "TradingView-style draggable Entry/SL/TP with live R:R + $-PnL before you click Execute." },
        ].map(f => (
          <div key={f.title} className="bg-[#11151d] border border-slate-800 rounded-2xl p-5 hover:border-slate-700 transition-all">
            <i className={`fa-solid ${f.icon} ${f.color} text-xl mb-3`}/>
            <div className="font-bold text-white text-sm mb-1">{f.title}</div>
            <div className="text-xs text-slate-400 leading-relaxed">{f.text}</div>
          </div>
        ))}
      </section>

      <footer className="relative z-10 border-t border-slate-800 px-8 py-5 text-center text-xs text-slate-600">
        Powered by Yahoo Finance · Claude Sonnet 4.5 · lightweight-charts v5
      </footer>
      {showSupport && <SupportFormModal onClose={() => setShowSupport(false)} />}
    </div>
  );
}
